import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Command } from "cmdk";
import { ArrowRight, Lightbulb, Loader2, Pencil, Sparkles } from "lucide-react";
import { api, type NlResult, type NotAChart } from "../../lib/api";
import { Dialog } from "../../components/ui/overlay";
import { Kbd } from "../../components/ui/primitives";
import { ASK_USAGE_KEY, formatTokens, useAskUsage } from "../account/AskUsageDialog";
import { askChanges } from "../builder/askChanges";
import { draftFromDefinition, draftToDefinition, useBuilder, usesFields, type Draft } from "../builder/store";

const EXAMPLES = [
  "Net sales by day last 30 days vs previous period",
  "Average check by location",
  "Tips by payment method last 7 days",
  "Items sold by category",
  "Orders by channel by week",
];

/** Changes that make sense for this widget, so none names data it doesn't have. */
function editExamples(d: Draft): string[] {
  const time = d.dimensions.find((x) => x.grain);
  const other = d.dimensions.some((x) => !x.grain);
  return [
    !time ? "Show the trend by week" : time.grain === "day" ? "By week instead" : "By day instead",
    d.compareTo ? "Remove the comparison" : "Compare with last year",
    other && !d.limit && "Top 10 only",
    d.viz.type === "table" ? "Show it as a bar chart" : "Show it as a table",
  ].filter((x): x is string => !!x);
}

/** A question as asked: the widget it changes (unset for a new one), and which builder and dialog opening it came from. */
interface Asked {
  prompt: string;
  before?: Draft;
  session: number;
  opening: number;
}

/** The answer's widget as a draft. Its date range is only a hint: the dashboard's range drives widgets. */
function toDraft(r: NlResult, before?: Draft): Draft {
  const { timeRange: _t, timePreset: _p, ...query } = r.query;
  // A kept chart type keeps its settings.
  const config = before && before.viz.type === r.visualization ? before.viz.config : {};
  return draftFromDefinition({ title: r.title, query: { ...query, pack: before?.pack }, visualization: { type: r.visualization, config } }, r.assumptions);
}

/**
 * ⌘K natural-language ask. Builds a semantic query (never SQL) that opens in the builder for review.
 * With a widget open in the builder, it changes that widget instead. `firstPack`: the data pack Ask
 * reads; a widget from another one can only be asked about as a new widget.
 */
export function AskBar({ open, onOpenChange, firstPack }: { open: boolean; onOpenChange: (o: boolean) => void; firstPack?: string }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [fresh, setFresh] = useState(false);
  const [notAChart, setNotAChart] = useState<NotAChart>();
  const [failed, setFailed] = useState<string>();
  const input = useRef<HTMLInputElement>(null);
  // Switching between changing the widget and a new one goes back to typing.
  const switchTo = (newWidget: boolean) => {
    setFresh(newWidget);
    setNotAChart(undefined);
    setFailed(undefined);
    input.current?.focus();
  };
  const { open: building, draft, openBuilder, applyAsk } = useBuilder();
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: api.catalog, staleTime: 5 * 60_000, enabled: open });
  const usage = useAskUsage(open);
  const editable = building && usesFields(draft) && (!draft.pack || draft.pack === firstPack);
  const editing = editable && !fresh;

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [open, onOpenChange]);

  // Each opening starts on the open widget, if there is one, and makes answers to earlier ones stale.
  const opening = useRef(0);
  useEffect(() => {
    if (!open) return;
    opening.current += 1;
    setFresh(false);
    setNotAChart(undefined);
    setFailed(undefined);
  }, [open]);

  // Everything an answer applies to is taken when the question is asked, never when the answer
  // comes back: by then the dialog, the mode or the widget in the builder may have changed.
  // Its error is kept here, not read off the mutation, so it shows only in the opening it was asked in.
  const stale = (v: Asked) => {
    const builder = useBuilder.getState();
    return v.opening !== opening.current || !open || v.session !== builder.session
      || (v.before && (!builder.open || builder.draft !== v.before));
  };
  const ask = useMutation({
    mutationFn: (v: Asked) => api.nl(v.prompt, v.before && draftToDefinition(v.before)),
    onError: (e, v) => {
      if (!stale(v)) setFailed((e as Error).message);
    },
    onSuccess: (r, v) => {
      if (stale(v)) return;
      if ("notAChart" in r) {
        setNotAChart(r);
        return;
      }
      onOpenChange(false);
      setText("");
      if (v.before) {
        const pack = catalog.data?.packs[0];
        const labels = new Map([...(pack?.metrics ?? []), ...(pack?.dimensions ?? [])].map((f) => [f.ref, f.label]));
        const after = toDraft(r, v.before);
        applyAsk(after, askChanges(v.before, after, (ref) => labels.get(ref) ?? ref));
      } else {
        openBuilder(toDraft(r));
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ASK_USAGE_KEY }),
  });
  const run = (prompt: string) => {
    setNotAChart(undefined);
    setFailed(undefined);
    if (prompt.length < 3 || ask.isPending) return;
    ask.mutate({ prompt, before: editing ? draft : undefined, session: useBuilder.getState().session, opening: opening.current });
  };

  const item = "flex cursor-default items-center gap-2 rounded-lg px-2 py-2 text-sm text-foreground data-[selected=true]:bg-surface-2";
  const group = "text-[11px] text-subtle [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1";
  const u = usage.data?.thisMonth;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Ask a question" initialFocus={input} className="top-[18%] w-[min(640px,92vw)] -translate-y-0 overflow-hidden">
      <Command shouldFilter={false} onKeyDown={(e) => e.key === "Enter" && run(text.trim())}>
        <div className="flex items-center gap-2 border-b border-border px-4 py-2 text-xs text-muted">
          {editing ? (
            <>
              <Pencil className="size-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">Changing <span className="font-medium text-foreground">{draft.title}</span></span>
              <button onClick={() => switchTo(true)} className="shrink-0 text-accent hover:underline">Ask about something else</button>
            </>
          ) : (
            <>
              <Sparkles className="size-3.5 shrink-0" />
              <span className="flex-1">New widget</span>
              {editable && <button onClick={() => switchTo(false)} className="shrink-0 text-accent hover:underline">Change “{draft.title}” instead</button>}
            </>
          )}
        </div>
        <div className="flex items-center gap-2 border-b border-border px-4">
          {ask.isPending ? <Loader2 className="size-4 animate-spin text-accent" /> : <Sparkles className="size-4 text-accent" />}
          <Command.Input
            ref={input}
            value={text}
            onValueChange={setText}
            placeholder={editing ? "What should change? e.g. by week instead" : "Ask about your data… e.g. net sales by hour yesterday"}
            className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-subtle"
            autoFocus
          />
          <Kbd>↵</Kbd>
        </div>
        {failed && <p className="px-4 pt-3 text-sm text-bad">{failed}</p>}
        {notAChart && (
          <div className="flex items-start gap-2 px-4 pt-3 text-sm">
            <Lightbulb className="mt-0.5 size-4 shrink-0 text-accent" />
            <p className="text-muted">{notAChart.reason} Try one of these instead:</p>
          </div>
        )}
        <Command.List className="max-h-72 overflow-y-auto p-2">
          {notAChart ? (
            <Command.Group heading="Charts that can help" className={group}>
              {notAChart.suggestions.map((s) => (
                <Command.Item key={s} value={s} onSelect={() => { setText(s); run(s); }} className={item}>
                  <ArrowRight className="size-3.5 text-subtle" /> {s}
                </Command.Item>
              ))}
            </Command.Group>
          ) : !text && (
            <Command.Group heading="Try" className={group}>
              {(editing ? editExamples(draft) : EXAMPLES).map((ex) => (
                <Command.Item key={ex} value={ex} onSelect={() => { setText(ex); run(ex); }} className={item}>
                  <ArrowRight className="size-3.5 text-subtle" /> {ex}
                </Command.Item>
              ))}
            </Command.Group>
          )}
        </Command.List>
        <div className="flex items-center gap-3 border-t border-border px-4 py-2 text-[11px] text-subtle">
          <span className="flex-1">Answers use certified metrics only. You'll review the result in the builder before it's saved.</span>
          {u && <span className="shrink-0">You've asked {u.asks} time{u.asks === 1 ? "" : "s"} this month · {formatTokens(u.tokens)} tokens</span>}
        </div>
      </Command>
    </Dialog>
  );
}
